import { useState, useEffect, Fragment } from 'react';

import { useAuth } from '../contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, FileText, MessageSquare, Bell, Clock, RefreshCw, History, Image, User, Trash2, Plus, Loader2, Tag, AlertTriangle, Globe, Archive, ArchiveRestore, Award, ChevronDown, BadgeCheck as BadgeIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { SyncPreviewModal } from '../components/SyncPreviewModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TrademarkDetail = () => {
  const { id } = useParams();
  const { token, user, isCliente, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [trademark, setTrademark] = useState(null);
  const [client, setClient] = useState(null);
  const [clients, setClients] = useState([]);
  const [statusCatalog, setStatusCatalog] = useState([]);
  const [notes, setNotes] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [events, setEvents] = useState([]);
  const [diffs, setDiffs] = useState([]);
  const [holders, setHolders] = useState([]);
  const [attorneys, setAttorneys] = useState([]);
  const [movements, setMovements] = useState([]); // ÉPICA 3.4.1: Trámites/Movimientos
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [savingClient, setSavingClient] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [noteVisibility, setNoteVisibility] = useState('INTERNAL');

  // Sync Modal State
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  // Delete trademark state - ÉPICA 3.4: Doble confirmación
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteConfirmCheckbox, setDeleteConfirmCheckbox] = useState(false);

  // Create client modal state
  const [createClientModalOpen, setCreateClientModalOpen] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [creatingClient, setCreatingClient] = useState(false);

  // P0 FIX TRÁMITE: Estado para expandir detalles de trámite
  const [expandedMovement, setExpandedMovement] = useState(null);

  // Responsive tabs state
  const [activeTab, setActiveTab] = useState("datos");

  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id]);

  const fetchData = async () => {
    try {
      // Obtener trademark
      const tmRes = await axios.get(`${API}/trademarks/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrademark(tmRes.data);

      // Obtener datos relacionados en paralelo
      const promises = [
        axios.get(`${API}/clients`, { 
          headers: { Authorization: `Bearer ${token}` },
          params: { limit: 1000 }
        }),
        axios.get(`${API}/status-catalog`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/notes?trademark_id=${id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/alerts?trademark_id=${id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/trademarks/${id}/events`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/trademarks/${id}/diffs`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/holders`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/attorneys`, { headers: { Authorization: `Bearer ${token}` } }),
        // ÉPICA 3.4.1: Obtener movimientos/trámite
        axios.get(`${API}/trademarks/${id}/movements`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
      ];

      const [clientsRes, statusRes, notesRes, alertsRes, eventsRes, diffsRes, holdersRes, attorneysRes, movementsRes] = await Promise.all(promises);
 
      const clientsData = clientsRes.data;
      const clientsList = (clientsData && clientsData.items) ? clientsData.items : (Array.isArray(clientsData) ? clientsData : []);
      setClients(clientsList);
      setStatusCatalog(statusRes.data);
      setNotes(notesRes.data);
      setAlerts(alertsRes.data);
      setEvents(eventsRes.data);
      setDiffs(diffsRes.data || []);
      setHolders(holdersRes.data);
      setAttorneys(attorneysRes.data);
      setMovements(movementsRes.data || []);

      // Obtener cliente asignado
      if (tmRes.data.client_id) {
        const assignedClient = clientsList.find(c => c.id === tmRes.data.client_id);
        setClient(assignedClient || null);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar la marca');
    } finally {
      setLoading(false);
    }
  };

  // Obtener nombre del estatus desde el catálogo
  const getStatusInfo = (statusId) => {
    if (!statusId) return { name: 'Sin estatus', color: '#9CA3AF' };
    const status = statusCatalog.find(s => s.id === statusId);
    if (status) {
      return { name: status.status_name, color: status.color };
    }
    // Si no encuentra en catálogo, mostrar el estatus crudo
    return { name: statusId, color: '#9CA3AF' };
  };

  const handleAssignClient = async (clientId) => {
    setSavingClient(true);
    try {
      await axios.put(`${API}/trademarks/${id}`, {
        client_id: clientId === 'none' ? null : clientId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Cliente asignado exitosamente');

      // Actualizar estado local
      const assignedClient = clients.find(c => c.id === clientId);
      setClient(assignedClient || null);
      setTrademark(prev => ({ ...prev, client_id: clientId === 'none' ? null : clientId }));
    } catch (error) {
      console.error('Error assigning client:', error);
      toast.error('Error al asignar cliente');
    } finally {
      setSavingClient(false);
    }
  };

  const handleCreateNote = async () => {
    if (!newNote.trim()) return;

    try {
      await axios.post(`${API}/notes`, {
        trademark_id: id,
        visibility: noteVisibility,
        content: newNote
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Nota creada exitosamente');
      setNewNote('');
      fetchData();
    } catch (error) {
      console.error('Error creating note:', error);
      toast.error('Error al crear la nota');
    }
  };

  const handleSync = async () => {
    if (syncing) return;

    setSyncing(true);
    try {
      // P1 FIX: Si ya hay cambios pendientes, intentar cargar el changeset existente
      // Evita re-scraped innecesario cuando el usuario solo quiere revisar
      if (trademark.has_pending_changes) {
        try {
          const latestRes = await axios.get(`${API}/trademarks/${id}/changesets/latest`, {
            headers: { Authorization: `Bearer ${token}` }
          });

          if (latestRes.data.success && latestRes.data.has_pending && latestRes.data.changeset) {
            const cs = latestRes.data.changeset;
            setPreviewData({
              changeset_id: cs.id,
              items_requires_approval: cs.items.filter(i => i.requires_approval),
              items_auto_apply: cs.items.filter(i => !i.requires_approval),
              summary: cs.summary_counts || { requires_approval: 0, auto_applied: 0, added: 0, filled: 0 },
              total_changes: cs.items.length,
              success: true
            });
            setSyncModalOpen(true);
            setSyncing(false);
            return;
          }
        } catch (e) {
          console.warn('Could not fetch existing changeset, falling back to sync', e);
        }
      }

      // P0 HOTFIX: SIEMPRE llamar /sync/preview primero
      const response = await axios.post(`${API}/trademarks/${id}/sync/preview`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const result = response.data;

      if (result.success) {
        // P0 HOTFIX: Si hay 0 cambios, mostrar toast y NO abrir modal
        if (result.total_changes === 0) {
          toast.info('Sin cambios detectados', {
            description: 'La marca está sincronizada con IMPI'
          });
          setSyncing(false);
          return;
        }

        // P0 HOTFIX: Si hay cambios (total_changes > 0), SIEMPRE abrir modal
        // El usuario DEBE revisar y aprobar TODOS los cambios
        setPreviewData(result);
        setSyncModalOpen(true);
      } else {
        toast.error(result.message || 'Error en sincronización');
      }
    } catch (error) {
      console.error('Error syncing:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || 'Error al sincronizar con IMPI';
      toast.error(errorMsg);
    } finally {
      setSyncing(false);
    }
  };

  const handleApplySync = async (acceptedIds, rejectedIds, changesetIdOverride = null) => {
    const cId = changesetIdOverride || previewData?.changeset_id;

    if (!cId) {
      console.error("No changeset ID available");
      return;
    }

    try {
      const response = await axios.post(`${API}/trademarks/${id}/sync/apply`, {
        changeset_id: cId,
        accepted_item_ids: acceptedIds,
        rejected_item_ids: rejectedIds
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        // Cerrar modal primero
        setSyncModalOpen(false);
        setPreviewData(null);

        // Mostrar toast
        toast.success('Cambios aplicados exitosamente', {
          description: response.data.message
        });

        // Esperar a que fetchData termine para actualizar la UI
        await fetchData();

        // Log para debugging
        console.log('[SYNC] Datos actualizados después de aplicar cambios');
      }
    } catch (error) {
      console.error('Error applying sync:', error);
      toast.error('Error al aplicar cambios');
      throw error; // Rethrow for modal to handle loading state
    }
  };

  // ÉPICA 3.4: Eliminar solo ADMIN con doble confirmación
  const handleDeleteTrademark = async () => {
    if (deleteConfirmText !== 'ELIMINAR' || !deleteConfirmCheckbox) {
      toast.error('Debe confirmar escribiendo ELIMINAR y marcando la casilla');
      return;
    }

    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/trademarks/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Marca eliminada exitosamente');
      navigate(-1);
    } catch (error) {
      console.error('Error deleting trademark:', error);
      toast.error('Error al eliminar la marca', {
        description: error.response?.data?.detail || 'Intente nuevamente'
      });
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
      setDeleteConfirmText('');
      setDeleteConfirmCheckbox(false);
    }
  };

  // P0 FIX: Cambiar estatus con update optimista + refetch
  const handleStatusChange = async (newStatusId) => {
    setSavingStatus(true);

    // P0: Update optimista - actualizar UI inmediatamente
    const previousStatus = trademark.estatus_interno_id;
    setTrademark(prev => ({ ...prev, estatus_interno_id: newStatusId }));

    try {
      await axios.put(`${API}/trademarks/${id}`, { estatus_interno_id: newStatusId }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // P0: Refetch del registro para garantizar sincronización
      const response = await axios.get(`${API}/trademarks/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setTrademark(response.data);

      toast.success('Estatus actualizado');
    } catch (error) {
      console.error('Error updating status:', error);

      // P0: Rollback en caso de error
      setTrademark(prev => ({ ...prev, estatus_interno_id: previousStatus }));

      toast.error('Error al actualizar estatus', {
        description: error.response?.data?.detail || 'Intente nuevamente'
      });
    } finally {
      setSavingStatus(false);
    }
  };

  const handleCreateClient = async () => {
    if (!newClientName.trim()) {
      toast.error('El nombre del cliente es requerido');
      return;
    }

    setCreatingClient(true);
    try {
      const response = await axios.post(`${API}/clients`, { nombre: newClientName.trim() }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const newClient = response.data;

      toast.success('Cliente creado exitosamente');

      // Add to clients list and assign to trademark
      setClients(prev => [...prev, newClient]);
      await handleAssignClient(newClient.id);

      // Close modal and reset
      setCreateClientModalOpen(false);
      setNewClientName('');
    } catch (error) {
      console.error('Error creating client:', error);
      toast.error('Error al crear el cliente', {
        description: error.response?.data?.detail || 'Intente nuevamente'
      });
    } finally {
      setCreatingClient(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      return new Date(date).toLocaleDateString('es-MX', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return date;
    }
  };

  // Obtener datos completos de holders por IDs
  const getHolderData = () => {
    if (!trademark?.holder_ids?.length) return [];
    return trademark.holder_ids
      .map(hid => holders.find(h => h.id === hid))
      .filter(Boolean);
  };

  // Obtener datos completos de attorneys por IDs
  const getAttorneyData = () => {
    if (!trademark?.attorney_ids?.length) return [];
    return trademark.attorney_ids
      .map(aid => attorneys.find(a => a.id === aid))
      .filter(Boolean);
  };

  // Obtener nombres de holders por IDs
  const getHolderNames = () => {
    if (!trademark?.holder_ids?.length) return 'Sin titular';
    return trademark.holder_ids
      .map(hid => holders.find(h => h.id === hid)?.nombre || hid)
      .join(', ');
  };

  // Obtener nombres de attorneys por IDs
  const getAttorneyNames = () => {
    if (!trademark?.attorney_ids?.length) return 'Sin apoderado';
    return trademark.attorney_ids
      .map(aid => attorneys.find(a => a.id === aid)?.nombre || aid)
      .join(', ');
  };

  // Construir URL de imagen (local o remota)
  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('/api')) {
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      const cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
      return `${cleanBase}${url}`;
    }
    return url;
  };

  const getMarciaUrl = (tm) => {
    if (!tm || !tm.expediente) return 'https://marcia.impi.gob.mx/';
    try {
      if (tm.fecha_presentacion) {
        let prefix = "RM"; // Por defecto Registro de Marca

        // Si tienes algún campo que indique aviso comercial, podrías cambiar prefix a "AC"
        if (tm.estatus_impi_crudo && tm.estatus_impi_crudo.toUpperCase().includes('AVISO COMERCIAL')) {
          prefix = "AC";
        }

        const dateObj = new Date(tm.fecha_presentacion);
        if (!isNaN(dateObj.getTime())) {
          const year = dateObj.getFullYear();
          const expPadded = String(tm.expediente).padStart(8, '0');
          return `https://marcia.impi.gob.mx/marcas/search/details/${prefix}${year}${expPadded}`;
        }
      }
    } catch (e) {
      console.error("Error generating Marcia URL", e);
    }
    // Fallback a la búsqueda general
    return `https://marcia.impi.gob.mx/#/search/results?q=${tm.expediente}`;
  };

  if (loading) {
    return (
      <>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      </>
    );
  }

  if (!trademark) {
    return (
      <>
        <div className="text-center py-12">
          <p className="text-gray-500">Marca no encontrada</p>
          <Button variant="outline" onClick={() => navigate('/trademarks')} className="mt-4">
            Volver a Marcas
          </Button>
        </div>
      </>
    );
  }

  const statusInfo = getStatusInfo(trademark.estatus_interno);

  return (
    <>
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            onClick={() => navigate('/trademarks')}
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver a Marcas
          </Button>

          {client && (
            <Button
              variant="ghost"
              onClick={() => navigate(`/clients/${client.id}`)}
              className="text-gray-600 hover:text-gray-900"
            >
              <User className="mr-2 h-4 w-4" />
              Volver al Cliente ({client.nombre})
            </Button>
          )}
        </div>

        {/* Responsive Header */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-4 md:p-6">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
              {/* Left Side: Brand Image & Core Info */}
              <div className="flex items-start gap-4 md:gap-6">
                <div className="relative shrink-0">
                  {(trademark.imagen_url || trademark.imagen_local_path) ? (
                    <img
                      src={getImageUrl(trademark.imagen_local_path || trademark.imagen_url)}
                      alt={trademark.denominacion || 'Marca'}
                      className="w-20 h-20 md:w-28 md:h-28 object-contain border border-gray-100 rounded-xl bg-gray-50 p-1"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                      }}
                    />
                  ) : null}
                  <div
                    className="w-20 h-20 md:w-28 md:h-28 border border-gray-200 rounded-xl bg-gradient-to-br from-gray-50 to-gray-200 items-center justify-center"
                    style={{ display: (trademark.imagen_url || trademark.imagen_local_path) ? 'none' : 'flex' }}
                  >
                    <span className="text-2xl md:text-3xl font-black text-gray-400 select-none">
                      {(trademark.denominacion || trademark.fallback_name || trademark.expediente || '?')[0].toUpperCase()}
                    </span>
                  </div>
                  {/* Status Badge floating on image for mobile */}
                  <div className="absolute -bottom-2 -right-2 lg:hidden">
                    <div
                      className="w-4 h-4 rounded-full border-2 border-white shadow-sm"
                      style={{ backgroundColor: statusInfo.color || '#9CA3AF' }}
                    />
                  </div>
                </div>

                <div className="flex-1 min-w-0 flex flex-col justify-center">
                  <h1 className="text-xl md:text-3xl font-black text-gray-900 leading-tight truncate mb-1 md:mb-2 flex items-center gap-3">
                    {trademark.denominacion ? (
                      trademark.denominacion
                    ) : (
                      <>
                        <span className="text-gray-400 italic font-medium">Sin denominación</span>
                        {trademark.fallback_name && (
                          <span className="inline-flex items-center px-3 py-1 mt-1 rounded text-sm font-bold bg-indigo-50 text-indigo-700 border border-indigo-100 not-italic tracking-wide">
                            {trademark.fallback_name}
                          </span>
                        )}
                      </>
                    )}
                  </h1>

                  <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs md:text-sm text-gray-500 font-medium">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] md:text-xs uppercase tracking-wider text-gray-400">Expediente</span>
                      <span className="font-mono text-gray-900 bg-gray-100 px-1.5 py-0.5 rounded">{trademark.expediente}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] md:text-xs uppercase tracking-wider text-gray-400">Registro</span>
                      <span className="font-mono text-gray-900 bg-gray-100 px-1.5 py-0.5 rounded">
                        {(trademark.registro && trademark.registro !== '' && trademark.registro !== 'Pendiente')
                          ? trademark.registro
                          : <span className="text-gray-400">Pendiente</span>}
                      </span>
                    </div>
                  </div>

                  {/* Sync Status - Subtle style */}
                  <div className="mt-3 flex items-center gap-2 text-[10px] md:text-xs text-gray-400">
                    <RefreshCw className={`h-3 w-3 ${syncing ? 'animate-spin text-indigo-500' : ''}`} />
                    <span>IMPI: {trademark.last_sync_at ? new Date(trademark.last_sync_at).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }) : 'Nunca'}</span>
                    {trademark.last_sync_status === 'error' && <span className="text-red-500 font-bold">• Error</span>}
                  </div>
                </div>
              </div>

              {/* Right Side: Action Clusters */}
              <div className="flex flex-col gap-3 lg:items-end w-full lg:w-auto mt-4 lg:mt-0 pt-4 lg:pt-0 border-t lg:border-t-0 border-gray-100">
                {/* Row 1: Primary Sync & Search Ops (Responsive Wrap) */}
                <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto lg:justify-end">
                  {!isCliente() && trademark.expediente && (
                    <Button
                      variant={trademark.has_pending_changes ? "default" : "outline"}
                      size="sm"
                      onClick={handleSync}
                      disabled={syncing}
                      className={`h-9 whitespace-nowrap px-4 font-bold flex-1 sm:flex-none ${trademark.has_pending_changes
                        ? 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-md shadow-indigo-100'
                        : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                    >
                      {syncing ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : trademark.has_pending_changes ? <AlertTriangle className="h-4 w-4 mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                      {syncing ? 'Sincronizando...' : trademark.has_pending_changes ? 'Revisar Cambios' : 'Sincronizar'}
                    </Button>
                  )}
                  {trademark.expediente && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(trademark.source_url || `https://acervomarcas.impi.gob.mx:8181/marcanet/vistas/common/datos/bsqExpedienteCompleto.pgi?expediente=${trademark.expediente}`, '_blank')}
                        className="h-9 whitespace-nowrap border-gray-200 text-gray-600 font-medium flex-1 sm:flex-none"
                      >
                        <FileText className="h-3.5 w-3.5 mr-2" />
                        Marcanet
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(getMarciaUrl(trademark), '_blank')}
                        className="h-9 whitespace-nowrap border-gray-200 text-gray-600 font-medium flex-1 sm:flex-none"
                      >
                        <Globe className="h-3.5 w-3.5 mr-2" />
                        MARCia
                      </Button>
                    </>
                  )}
                </div>

                {/* Row 2: Internal Ops & Status */}
                <div className="flex flex-wrap items-center gap-2">
                  {!isCliente() ? (
                    <Select
                      value={trademark.estatus_interno_id || 'none'}
                      onValueChange={(val) => handleStatusChange(val === 'none' ? "" : val)}
                      disabled={savingStatus}
                    >
                      <SelectTrigger className="w-full md:w-[220px] h-9 bg-gray-50/50 border-gray-200 font-bold text-xs ring-offset-white">
                        <SelectValue placeholder="Estado interno" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin estatus</SelectItem>
                        {statusCatalog.map(status => (
                          <SelectItem key={status.id} value={status.id}>
                            <div className="flex items-center gap-2">
                              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: status.color || '#6B7280' }} />
                              <span className="text-xs font-bold">{status.status_name}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div
                      className="px-4 py-1.5 rounded-full text-xs font-black flex items-center gap-2 shadow-sm border"
                      style={{
                        backgroundColor: `${statusInfo.color}10`,
                        color: statusInfo.color,
                        borderColor: `${statusInfo.color}30`
                      }}
                    >
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: statusInfo.color }} />
                      {statusInfo.name.toUpperCase()}
                    </div>
                  )}

                  <div className="flex items-center gap-1 ml-auto">
                    {!isCliente() && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={async () => {
                          try {
                            await axios.put(`${API}/trademarks/${id}`, { is_active: !trademark.is_active }, { headers: { Authorization: `Bearer ${token}` } });
                            toast.success(trademark.is_active ? 'Marca archivada' : 'Marca desarchivada');
                            fetchData();
                          } catch (error) { toast.error('Error al archivar'); }
                        }}
                        className={`h-9 w-9 border border-gray-200 ${!trademark.is_active ? 'text-green-600 bg-green-50/50' : 'text-gray-400'}`}
                      >
                        {!trademark.is_active ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                      </Button>
                    )}
                    {isAdmin() && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => { setDeleteConfirmText(''); setDeleteConfirmCheckbox(false); setDeleteDialogOpen(true); }}
                        className="h-9 w-9 text-red-400 hover:text-red-600 hover:bg-red-50 border border-transparent hover:border-red-100"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Row 3: Client Relation */}
                <div className="flex items-center gap-2 w-full lg:w-auto mt-2">
                  <div className="flex items-center gap-2 bg-gray-50/50 border border-gray-200 rounded-lg px-2 py-1 w-full lg:w-max">
                    <User className="h-3.5 w-3.5 text-gray-400 shrink-0 ml-1" />
                    {isCliente() ? (
                      <span className="text-xs font-bold text-gray-900 px-2 py-1 truncate">{client?.nombre || '—'}</span>
                    ) : (
                      <div className="flex flex-1 sm:flex-initial gap-1 items-center">
                        <Select
                          value={trademark.client_id || 'none'}
                          onValueChange={handleAssignClient}
                          disabled={savingClient}
                        >
                          <SelectTrigger className="h-8 border-none bg-transparent shadow-none font-bold text-xs focus:ring-0 px-2 flex-1 w-full md:w-[160px]">
                            <SelectValue placeholder="Asignar cliente..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Sin asignación</SelectItem>
                            {clients.map(c => (
                              <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setCreateClientModalOpen(true)}
                          className="h-7 w-7 shrink-0 text-indigo-600 hover:bg-indigo-50 rounded-full"
                          title="Crear cliente"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="flex flex-col gap-3">
            {/* Mobile Tab Selector */}
            <div className="block sm:hidden w-full">
              <Select value={activeTab} onValueChange={setActiveTab}>
                <SelectTrigger className="w-full bg-white h-12 font-bold shadow-sm border-gray-200">
                  <SelectValue placeholder="Seleccionar vista" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="datos"><div className="flex items-center"><FileText className="h-4 w-4 mr-2 text-gray-400" />Datos</div></SelectItem>
                  <SelectItem value="tramite"><div className="flex items-center"><Clock className="h-4 w-4 mr-2 text-gray-400" />Trámite ({movements.length})</div></SelectItem>
                  <SelectItem value="notas"><div className="flex items-center"><MessageSquare className="h-4 w-4 mr-2 text-gray-400" />Notas ({notes.length})</div></SelectItem>
                  <SelectItem value="alertas"><div className="flex items-center"><Bell className="h-4 w-4 mr-2 text-gray-400" />Alertas ({alerts.length})</div></SelectItem>
                  <SelectItem value="eventos"><div className="flex items-center"><Clock className="h-4 w-4 mr-2 text-gray-400" />Eventos ({events.length})</div></SelectItem>
                  {!isCliente() && (
                    <SelectItem value="historial"><div className="flex items-center"><History className="h-4 w-4 mr-2 text-gray-400" />Historial</div></SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Desktop Tabs */}
            <div className="hidden sm:block overflow-x-auto no-scrollbar w-full">
              <TabsList className="bg-gray-100/80 p-1 inline-flex w-max">
                <TabsTrigger value="datos" className="data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm text-xs font-bold py-1.5 px-3">
                  <FileText className="h-3.5 w-3.5 mr-1.5" />
                  Datos
                </TabsTrigger>
                <TabsTrigger value="tramite" className="data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm text-xs font-bold py-1.5 px-3">
                  <Clock className="h-3.5 w-3.5 mr-1.5" />
                  Trámite ({movements.length})
                </TabsTrigger>
                <TabsTrigger value="notas" className="data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm text-xs font-bold py-1.5 px-3">
                  <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
                  Notas ({notes.length})
                </TabsTrigger>
                <TabsTrigger value="alertas" className="data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm text-xs font-bold py-1.5 px-3">
                  <Bell className="h-3.5 w-3.5 mr-1.5" />
                  Alertas ({alerts.length})
                </TabsTrigger>
                <TabsTrigger value="eventos" className="data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm text-xs font-bold py-1.5 px-3">
                  <Clock className="h-3.5 w-3.5 mr-1.5" />
                  Eventos ({events.length})
                </TabsTrigger>
                {!isCliente() && (
                  <TabsTrigger value="historial" className="data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm text-xs font-bold py-1.5 px-3">
                    <History className="h-3.5 w-3.5 mr-1.5" />
                    Historial
                  </TabsTrigger>
                )}
              </TabsList>
            </div>
          </div>

          {/* Datos */}
          <TabsContent value="datos" className="mt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {/* Información General */}
              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4">Información General</h3>
                <dl className="space-y-4">
                  {trademark.tipo && trademark.tipo !== 'N/A' && (
                    <div>
                      <dt className="text-[10px] text-gray-500 uppercase font-black">Tipo de Marca</dt>
                      <dd className="text-sm text-gray-900 font-bold mt-1 bg-gray-50 p-2 rounded-lg inline-block border border-gray-100">{trademark.tipo}</dd>
                    </div>
                  )}

                  <div>
                    <dt className="text-[10px] text-gray-500 uppercase font-black">
                      CLASE{!trademark.clases || trademark.clases.length <= 1 ? '' : 'S'}
                    </dt>
                    <dd className="flex flex-wrap gap-1.5 mt-2">
                      {trademark.clases && trademark.clases.length > 0 ? (
                        trademark.clases.map(c => (
                          <span key={c} className="bg-indigo-50 text-indigo-700 text-xs font-black px-2.5 py-1 rounded-md border border-indigo-100">{c}</span>
                        ))
                      ) : (
                        <span className="text-sm text-gray-400 italic">No especificada</span>
                      )}
                    </dd>
                  </div>

                  {(trademark.descripcion || trademark.goods_services || trademark.descripcion_productos) && (
                    <div>
                      <dt className="text-[10px] text-gray-500 uppercase font-black">Productos y Servicios</dt>
                      <dd className="text-sm text-gray-900 mt-2 font-medium bg-gray-50/50 p-3 rounded-xl border border-gray-100 leading-relaxed max-h-48 overflow-y-auto no-scrollbar">
                        {trademark.descripcion || trademark.descripcion_productos || (
                          trademark.goods_services ? Object.values(trademark.goods_services).join('; ') : '—'
                        )}
                      </dd>
                    </div>
                  )}

                  {trademark.trademark_description && (
                    <div>
                      <dt className="text-[10px] text-gray-500 uppercase font-black">Descripción Visual</dt>
                      <dd className="text-sm text-gray-700 mt-2 italic bg-gray-50/50 p-3 rounded-xl border border-dashed border-gray-200">
                        {trademark.trademark_description}
                      </dd>
                    </div>
                  )}

                  {trademark.establishment && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Establecimiento</h4>
                      <div className="space-y-3">
                        {trademark.establishment.address && (
                          <div className="bg-gray-50/50 p-2 rounded-lg">
                            <dt className="text-[10px] text-gray-400 uppercase font-bold">Dirección</dt>
                            <dd className="text-xs text-gray-700 mt-0.5">{trademark.establishment.address}</dd>
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-3">
                          {trademark.establishment.city && (
                            <div className="bg-gray-50/50 p-2 rounded-lg">
                              <dt className="text-[10px] text-gray-400 uppercase font-bold">Población</dt>
                              <dd className="text-xs text-gray-700 mt-0.5">{trademark.establishment.city}</dd>
                            </div>
                          )}
                          {trademark.establishment.postal_code && (
                            <div className="bg-gray-50/50 p-2 rounded-lg">
                              <dt className="text-[10px] text-gray-400 uppercase font-bold">C.P.</dt>
                              <dd className="text-xs text-gray-700 mt-0.5 font-mono">{trademark.establishment.postal_code}</dd>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </dl>
              </div>

              {/* Fechas Importantes */}
              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4">Fechas Clave</h3>
                <div className="space-y-3">
                  {[
                    { label: 'Presentación', date: trademark.fecha_presentacion, icon: <Plus className="h-3 w-3" />, color: 'bg-blue-50 text-blue-700' },
                    { label: 'Primer Uso', date: trademark.fecha_primer_uso, icon: <Tag className="h-3 w-3" />, color: 'bg-orange-50 text-orange-700' },
                    { label: 'Concesión', date: trademark.fecha_concesion, icon: <Award className="h-3 w-3" />, color: 'bg-green-50 text-green-700' },
                    { label: 'Vigencia', date: trademark.fecha_vigencia, icon: <Clock className="h-3 w-3" />, color: 'bg-purple-50 text-purple-700' }
                  ].map((item, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-gray-50/50 border border-gray-100 hover:bg-white hover:shadow-sm transition-all duration-200">
                      <div className="flex items-center gap-3">
                        <div className={`p-1.5 rounded-lg ${item.color}`}>
                          {item.icon}
                        </div>
                        <span className="text-xs font-bold text-gray-600">{item.label}</span>
                      </div>
                      <span className="text-xs font-black text-gray-900 font-mono">
                        {formatDate(item.date) || <span className="text-gray-300">N/A</span>}
                      </span>
                    </div>
                  ))}

                  {trademark.fecha_publicacion && (
                    <div className="flex items-center justify-between p-3 rounded-xl bg-indigo-50/30 border border-indigo-100 mt-2">
                      <div className="flex items-center gap-3">
                        <div className="p-1.5 rounded-lg bg-indigo-100 text-indigo-700">
                          <Globe className="h-3 w-3" />
                        </div>
                        <span className="text-xs font-bold text-gray-600">Publicación Gaceta</span>
                      </div>
                      <span className="text-xs font-black text-gray-900 font-mono">
                        {formatDate(trademark.fecha_publicacion)}
                      </span>
                    </div>
                  )}
                </div>

                <div className="mt-6 pt-6 border-t border-gray-100">
                  <dt className="text-[10px] text-gray-400 uppercase font-black mb-1">Estatus IMPI</dt>
                  <dd className="text-sm text-gray-900 font-medium bg-gray-50 px-3 py-2 rounded-lg border border-gray-100 truncate">
                    {trademark.estatus_impi_crudo || '—'}
                  </dd>
                </div>
              </div>

              {/* Titulares & Apoderados */}
              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                  <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4">Titular(es)</h3>
                  {getHolderData().length === 0 ? (
                    <div className="text-center py-6 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                      <p className="text-xs text-gray-400 font-bold">Sin información</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {getHolderData().map((holder, idx) => (
                        <div key={holder.id || idx} className="bg-gray-50/50 p-4 rounded-xl border border-gray-100">
                          <dd className="text-sm text-gray-900 font-black mb-3">{holder.nombre || '—'}</dd>
                          <div className="space-y-2">
                            <div className="flex flex-col">
                              <span className="text-[9px] text-gray-400 uppercase font-black">Dirección</span>
                              <span className="text-xs text-gray-700 leading-normal">{holder.direccion || holder.direccion_completa || '—'}</span>
                            </div>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                              <div>
                                <span className="text-[9px] text-gray-400 uppercase font-black">Nacionalidad</span>
                                <span className="text-xs text-gray-700 block mt-0.5">{holder.nacionalidad || holder.pais || '—'}</span>
                              </div>
                              <div>
                                <span className="text-[9px] text-gray-400 uppercase font-black">RFC</span>
                                <span className="text-xs text-gray-900 font-mono block mt-0.5">{holder.rfc || '—'}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                  <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4">Apoderado(s)</h3>
                  {getAttorneyData().length === 0 ? (
                    <div className="text-center py-6 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                      <p className="text-xs text-gray-400 font-bold">Sin información</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {getAttorneyData().map((attorney, idx) => (
                        <div key={attorney.id || idx} className="bg-gray-50/50 p-4 rounded-xl border border-gray-100">
                          <dd className="text-sm text-gray-900 font-black mb-3">{attorney.nombre || '—'}</dd>
                          <div className="space-y-2">
                            <div className="flex flex-col">
                              <span className="text-[9px] text-gray-400 uppercase font-black">Dirección</span>
                              <span className="text-xs text-gray-700 leading-normal">{attorney.direccion || attorney.direccion_completa || '—'}</span>
                            </div>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                              <div>
                                <span className="text-[9px] text-gray-400 uppercase font-black">RFC</span>
                                <span className="text-xs text-gray-900 font-mono block mt-0.5">{attorney.rfc || '—'}</span>
                              </div>
                              <div>
                                <span className="text-[9px] text-gray-400 uppercase font-black">Email</span>
                                <span className="text-xs text-gray-700 truncate block mt-0.5" title={attorney.email}>{attorney.email || '—'}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

            </div>
          </TabsContent>

          {/* Notas */}
          <TabsContent value="notas" className="mt-0">
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <div className="space-y-6">
                <div className="flex flex-col gap-4">
                  <Textarea
                    placeholder="Escribe comentarios internos o para el cliente..."
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    className="min-h-[120px] bg-gray-50/50 border-gray-200 rounded-xl p-4 text-sm focus-visible:ring-indigo-500"
                  />
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                    {!isCliente() ? (
                      <div className="flex items-center gap-2 w-full sm:w-auto">
                        <span className="text-[10px] uppercase font-black text-gray-400 shrink-0">Visible para:</span>
                        <Select value={noteVisibility} onValueChange={setNoteVisibility}>
                          <SelectTrigger className="h-8 text-xs font-bold border-gray-200 bg-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="INTERNAL">Solo Equipo Interno</SelectItem>
                            <SelectItem value="CLIENT">Visible para Cliente</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    ) : <div />}
                    <Button
                      onClick={handleCreateNote}
                      disabled={!newNote.trim()}
                      className="w-full sm:w-auto bg-gray-900 h-9 px-6 font-bold text-xs"
                    >
                      Publicar Nota
                    </Button>
                  </div>
                </div>

                <div className="pt-6 border-t border-gray-100">
                  <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4">Cronología de Notas</h4>
                  {notes.length === 0 ? (
                    <div className="text-center py-10 bg-gray-50/50 rounded-xl border border-dashed border-gray-200">
                      <MessageSquare className="h-8 w-8 text-gray-200 mx-auto mb-2" />
                      <p className="text-xs text-gray-400 font-bold">Aún no hay mensajes o notas registradas</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {notes.map(note => (
                        <div key={note.id} className={`p-4 rounded-xl border flex flex-col gap-2 ${note.visibility === 'INTERNAL' ? 'bg-gray-50/50 border-gray-100 border-l-4 border-l-gray-300' : 'bg-white border-blue-50 border-l-4 border-l-blue-400 shadow-sm'}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] text-gray-400 font-black">{new Date(note.created_at).toLocaleDateString()}</span>
                              <span className={`text-[9px] px-1.5 py-0.5 rounded font-black uppercase ring-1 ring-inset ${note.visibility === 'INTERNAL' ? 'bg-gray-100 text-gray-600 ring-gray-200' : 'bg-blue-50 text-blue-600 ring-blue-100'}`}>
                                {note.visibility === 'INTERNAL' ? 'Interna' : 'Cliente'}
                              </span>
                            </div>
                          </div>
                          <p className="text-sm text-gray-700 leading-relaxed font-medium whitespace-pre-wrap">{note.content}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Alertas */}
          <TabsContent value="alertas" className="mt-0">
            {alerts.length === 0 ? (
              <div className="bg-white rounded-xl border border-dashed border-gray-200 p-12 text-center">
                <Bell className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                <p className="text-sm font-bold text-gray-400">Sin alertas activas</p>
                <p className="text-[10px] text-gray-300 mt-1 uppercase tracking-wider font-black">
                  Las alertas aparecen 60 días antes o después del vencimiento
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3">
                {alerts.map(alert => {
                  const isOverdue = alert.is_overdue;
                  // Label del tipo
                  const typeLabels = {
                    VIGENCIA: 'Renovación', RENEWAL: 'Renovación', EXPIRY: 'Renovación',
                    DECLARATION_OF_USE: 'Declaración de uso', CUSTOM: 'Personalizada'
                  };
                  const typeLabel = typeLabels[alert.alert_type] || alert.alert_type;
                  // Colores: amarillo = pendiente (pre-window), rojo = vencida (post-window)
                  const badgeColor = isOverdue
                    ? 'bg-red-100 text-red-700 border-red-200'
                    : 'bg-amber-100 text-amber-700 border-amber-200';
                  const cardBorder = isOverdue ? 'border-red-100 hover:border-red-200' : 'border-amber-100 hover:border-amber-200';
                  const iconBg = isOverdue ? 'bg-red-50 text-red-500' : 'bg-amber-50 text-amber-500';

                  // Calcular días
                  const dueDate = alert.due_date ? new Date(alert.due_date) : null;
                  const daysVal = alert.days_remaining ?? alert.days_until_due;
                  const daysLabel = isOverdue
                    ? `Vencida hace ${Math.abs(daysVal ?? 0)} días`
                    : daysVal !== undefined ? `${daysVal} días restantes` : '';

                  return (
                    <div key={alert.id} className={`bg-white rounded-xl border ${cardBorder} p-4 shadow-sm transition-colors`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <div className={`p-2 rounded-lg shrink-0 mt-0.5 ${iconBg}`}>
                            <Bell className="h-4 w-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            {/* Tag tipo + estado */}
                            <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                              <span className={`text-[10px] font-black px-2 py-0.5 rounded-full border ${badgeColor}`}>
                                {typeLabel}
                              </span>
                              {isOverdue && (
                                <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-100 uppercase">
                                  Vencida
                                </span>
                              )}
                            </div>
                            {/* Descripción */}
                            <p className="text-sm font-bold text-gray-900 truncate">{alert.description}</p>
                            {/* Fechas */}
                            <div className="flex flex-wrap items-center gap-3 mt-2">
                              {dueDate && (
                                <div className="flex items-center gap-1 bg-gray-50 px-2 py-0.5 rounded-md border border-gray-100">
                                  <span className="text-[9px] text-gray-400 uppercase font-black">Límite</span>
                                  <span className="text-[10px] text-gray-700 font-black">
                                    {dueDate.toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: 'numeric' })}
                                  </span>
                                </div>
                              )}
                              {daysLabel && (
                                <span className={`text-[10px] font-bold ${isOverdue ? 'text-red-600' : 'text-amber-600'}`}>
                                  {daysLabel}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        {/* Estado */}
                        <div className={`shrink-0 px-2 py-1 rounded-full text-[9px] font-black uppercase border ${alert.status === 'COMPLETED' ? 'bg-green-50 text-green-600 border-green-100' :
                          alert.status === 'DISMISSED' ? 'bg-gray-50 text-gray-400 border-gray-100' :
                            isOverdue ? 'bg-red-50 text-red-600 border-red-100' : 'bg-amber-50 text-amber-600 border-amber-100'
                          }`}>
                          {alert.status === 'COMPLETED' ? 'Completada' :
                            alert.status === 'DISMISSED' ? 'Omitida' :
                              isOverdue ? 'Vencida' : 'Pendiente'}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>


          {/* Eventos */}
          <TabsContent value="eventos" className="mt-0">
            {events.length === 0 ? (
              <div className="bg-white rounded-xl border border-dashed border-gray-200 p-12 text-center">
                <History className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                <p className="text-sm font-bold text-gray-400">Sin actividad reciente registrada</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                <div className="relative space-y-0 pb-2">
                  <div className="absolute left-1 top-2 bottom-0 w-px bg-gray-100" />
                  {events.map((event, index) => (
                    <div key={event.id} className="relative pl-8 pb-8 last:pb-0">
                      <div className={`absolute left-0 top-1.5 h-2 w-2 rounded-full border-2 border-white shadow-sm ring-2 ${event.event_type === 'SYNC' ? 'ring-indigo-100 bg-indigo-500' : 'ring-gray-100 bg-gray-400'}`} />
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-[9px] font-black text-indigo-500 uppercase tracking-widest">{event.event_type}</span>
                        <span className="text-[10px] text-gray-400 font-mono">{formatDate(event.created_at)}</span>
                      </div>
                      <p className="text-sm text-gray-700 font-medium leading-relaxed">{event.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent >

          {/* Historial de cambios */}
          {!isCliente() && (
            <TabsContent value="historial" className="mt-0">
              {diffs.length === 0 ? (
                <div className="bg-white rounded-xl border border-dashed border-gray-200 p-12 text-center">
                  <RefreshCw className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                  <p className="text-sm font-bold text-gray-400">No hay cambios registrados en IMPI</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {diffs.map(diff => (
                    <div key={diff.id} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex flex-col">
                          <span className="text-[10px] text-gray-400 uppercase font-black tracking-widest">Sincronización</span>
                          <span className="text-xs font-black text-indigo-600">#{diff.id.substring(0, 8)}</span>
                        </div>
                        <div className="flex flex-col items-end">
                          <span className="text-[10px] text-gray-400 font-mono mb-1">{new Date(diff.detected_at).toLocaleString()}</span>
                          <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase ring-1 ring-inset ${diff.no_changes ? 'bg-gray-100 text-gray-500 ring-gray-200' : 'bg-green-50 text-green-700 ring-green-100'}`}>
                            {diff.no_changes ? 'Sin Cambios' : `${diff.changes?.length || 0} Registrados`}
                          </span>
                        </div>
                      </div>

                      {diff.changes && diff.changes.length > 0 && (
                        <div className="space-y-2 pt-3 border-t border-gray-50">
                          {diff.changes.map((change, idx) => (
                            <div key={idx} className="bg-gray-50/50 p-3 rounded-lg border border-gray-100">
                              <span className="text-[10px] text-gray-400 font-black uppercase block mb-1.5">{change.field}</span>
                              <div className="flex flex-col gap-1.5">
                                {change.status === 'changed' ? (
                                  <>
                                    <div className="text-[11px] text-red-500 bg-red-50/50 px-2 py-1 rounded italic line-through truncate">{change.before || '—'}</div>
                                    <div className="text-[11px] text-green-700 bg-green-50/50 px-2 py-1 rounded font-bold break-words">{change.after}</div>
                                  </>
                                ) : (
                                  <div className="text-[11px] text-indigo-700 bg-indigo-50/50 px-2 py-1 rounded font-bold">{change.status === 'added' ? '+' : ''} {change.after || change.before}</div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          )}

          {/* Trámite/Movimientos */}
          <TabsContent value="tramite" className="mt-0">
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <div className="mb-6">
                <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-1">Ruta del Expediente</h3>
                <p className="text-[10px] text-gray-400 font-bold uppercase">Datos extraídos directamente de Marcanet</p>
              </div>

              {movements.length === 0 ? (
                <div className="text-center py-16 bg-gray-50/50 rounded-2xl border border-dashed border-gray-200">
                  <Clock className="h-12 w-12 text-gray-200 mx-auto mb-4" />
                  <p className="text-sm font-bold text-gray-400">Sin movimientos reportados</p>
                  <Button variant="outline" size="sm" onClick={handleSync} className="mt-4 h-8 text-[10px] font-black border-indigo-200 text-indigo-700">Sincronizar ahora</Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {movements.map((mov, idx) => (
                    <div key={mov.id || idx} className={`rounded-xl border transition-all duration-200 ${expandedMovement === mov.id ? 'border-indigo-500 shadow-md ring-1 ring-indigo-50' : 'border-gray-200 hover:border-gray-300'}`}>
                      <div
                        className={`p-4 cursor-pointer flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${expandedMovement === mov.id ? 'bg-indigo-50/20' : 'bg-white'}`}
                        onClick={() => setExpandedMovement(expandedMovement === mov.id ? null : mov.id)}
                      >
                        {/* Left Side: Info */}
                        <div className="flex flex-col gap-2 flex-1 min-w-0">
                          {/* Row 1: Folio + Año */}
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="px-2 py-0.5 rounded bg-gray-900 text-[11px] font-bold text-white tracking-wider">
                              {mov.folio_entrada || mov.actuacion || 'S/F'}
                            </div>
                            {mov.anio_recepcion && (
                              <span className="text-[11px] text-gray-500 font-bold bg-white">{mov.anio_recepcion}</span>
                            )}
                          </div>

                          {/* Row 2: Descripción */}
                          <h4 className="text-[13px] font-black text-gray-900 leading-tight uppercase mt-1">{mov.descripcion || mov.tipo}</h4>

                          {/* Row 3: Fechas + oficios */}
                          <div className="flex items-center gap-3 mt-1">
                            {(mov.fecha_inicio || mov.fecha) && (
                              <div className="flex items-center gap-1.5">
                                <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Inicio</span>
                                <span className="text-[11px] text-gray-600 font-bold">{formatDate(mov.fecha_inicio || mov.fecha)}</span>
                              </div>
                            )}
                            {mov.correo_electronico && (
                              <div className="flex items-center gap-1.5 hidden md:flex">
                                <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Correo</span>
                                <span className="text-[11px] text-gray-600 font-bold max-w-[150px] truncate" title={mov.correo_electronico}>{mov.correo_electronico}</span>
                              </div>
                            )}
                            {mov.oficios?.length > 0 && (
                              <span className="text-[10px] bg-red-50 text-red-600 px-1.5 py-0.5 rounded font-black border border-red-100 flex items-center gap-1 uppercase">
                                <Archive className="h-3 w-3" />
                                {mov.oficios.length} Oficio{mov.oficios.length > 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Right Side: Actions or Chevron */}
                        <div className="flex gap-2 max-w-full overflow-x-auto no-scrollbar pt-3 sm:pt-0 shrink-0 border-t sm:border-t-0 border-gray-100 mt-3 sm:mt-0 items-center justify-end">
                          {(mov.imagen_url || mov.document_url || mov.url) && (
                            <button
                              onClick={(e) => { e.stopPropagation(); window.open(mov.imagen_url || mov.document_url || mov.url, '_blank'); }}
                              className="shrink-0 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-indigo-600 rounded-lg text-xs font-semibold shadow-sm transition-all"
                            >
                              <FileText className="h-3.5 w-3.5" />
                              Ver imagen
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedMovement(expandedMovement === mov.id ? null : mov.id);
                            }}
                            className="shrink-0 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-gray-900 border border-transparent text-white hover:bg-gray-800 rounded-lg text-xs font-semibold shadow-sm transition-all"
                          >
                            <Archive className="h-3.5 w-3.5" />
                            Ver oficios
                            <ChevronDown className={`h-3 w-3 ml-0.5 opacity-70 transition-transform ${expandedMovement === mov.id ? 'rotate-180' : ''}`} />
                          </button>
                        </div>
                      </div>

                      {expandedMovement === mov.id && (
                        <div className="px-4 pb-4 bg-white rounded-b-xl border-t border-gray-100 animate-in slide-in-from-top-2 duration-200">
                          <div className="pt-4 space-y-4">
                            {/* Detailed dates */}
                            <div className="grid grid-cols-2 gap-3 pb-4 border-b border-gray-50">
                              <div className="bg-gray-50 p-2 rounded-lg">
                                <span className="text-[9px] text-gray-400 font-black uppercase block">Inicio</span>
                                <span className="text-xs font-bold text-gray-700">{formatDate(mov.fecha_inicio || mov.fecha)}</span>
                              </div>
                              <div className="bg-gray-50 p-2 rounded-lg">
                                <span className="text-[9px] text-gray-400 font-black uppercase block">Conclusión</span>
                                <span className="text-xs font-bold text-gray-700">{formatDate(mov.fecha_conclusion) || '—'}</span>
                              </div>
                            </div>

                            {/* Oficios associated */}
                            {mov.oficios?.length > 0 ? (
                              <div className="space-y-3">
                                <h5 className="text-[10px] font-black text-gray-400 uppercase tracking-widest pl-1">Documentos y Oficios</h5>
                                {mov.oficios.map((oficio, oIdx) => (
                                  <div key={oficio.id || oIdx} className="bg-gray-50/60 border border-gray-100 p-3 rounded-xl">
                                    <p className="text-xs font-black text-gray-900 mb-2 leading-snug">
                                      {oficio.descripcion_oficio || '—'}
                                    </p>
                                    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mb-2">
                                      <div>
                                        <span className="text-[9px] text-gray-400 uppercase font-black block">Número de oficio</span>
                                        <span className="text-[10px] font-mono font-bold text-gray-700">{oficio.numero_oficio || '—'}</span>
                                      </div>
                                      <div>
                                        <span className="text-[9px] text-gray-400 uppercase font-black block">Fecha del oficio</span>
                                        <span className="text-[10px] font-bold text-gray-700">{formatDate(oficio.fecha_oficio) || '—'}</span>
                                      </div>
                                    </div>
                                    {oficio.estado_notificacion && (
                                      <p className="text-[10px] text-gray-500 italic mb-2 leading-relaxed border-t border-gray-100 pt-2">
                                        {oficio.estado_notificacion}
                                      </p>
                                    )}
                                    {(oficio.imagen_url || oficio.document_url || oficio.url) && (
                                      <button
                                        onClick={() => window.open(oficio.imagen_url || oficio.document_url || oficio.url, '_blank')}
                                        className="text-[10px] font-black text-indigo-600 hover:text-indigo-800 flex items-center gap-1 border border-indigo-100 bg-indigo-50 hover:bg-indigo-100 px-2.5 py-1 rounded-md transition-colors mt-1"
                                      >
                                        <FileText className="h-3 w-3" />
                                        Ver
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : <p className="text-xs text-center text-gray-400 italic py-2">Sin oficios publicados para este folio</p>}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Sync Modal */}
        <SyncPreviewModal
          isOpen={syncModalOpen}
          onClose={setSyncModalOpen}
          previewData={previewData}
          onApply={handleApplySync}
        />

        {/* Dialog de confirmación de eliminación */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="text-red-600">⚠️ Eliminar marca permanentemente</AlertDialogTitle>
              <AlertDialogDescription className="space-y-4 text-gray-700">
                <p>Esta acción <strong className="text-gray-900">NO SE PUEDE DESHACER</strong>. Se eliminarán todos los datos de la marca
                  <span className="font-semibold text-gray-900"> {trademark?.denominacion || trademark?.expediente}</span>, incluyendo:</p>
                <ul className="list-disc pl-5 text-sm text-gray-700">
                  <li>Todas las notas</li>
                  <li>Todas las alertas</li>
                  <li>Todo el historial</li>
                  <li>Todos los snapshots</li>
                </ul>

                <div className="space-y-3 pt-4 border-t border-gray-200">
                  <div>
                    <Label htmlFor="detail-delete-confirm" className="text-gray-700">Escriba <strong className="text-gray-900">ELIMINAR</strong> para confirmar:</Label>
                    <Input
                      id="detail-delete-confirm"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder="ELIMINAR"
                      className="mt-1"
                      data-testid="detail-delete-confirm-input"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="detail-delete-checkbox"
                      checked={deleteConfirmCheckbox}
                      onCheckedChange={setDeleteConfirmCheckbox}
                      data-testid="detail-delete-confirm-checkbox"
                    />
                    <Label htmlFor="detail-delete-checkbox" className="text-sm text-gray-700">
                      Entiendo que esta acción es irreversible
                    </Label>
                  </div>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleteLoading} onClick={() => { setDeleteConfirmText(''); setDeleteConfirmCheckbox(false); }}>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteTrademark}
                disabled={deleteLoading || deleteConfirmText !== 'ELIMINAR' || !deleteConfirmCheckbox}
                className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              >
                {deleteLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Eliminando...
                  </>
                ) : (
                  'Eliminar permanentemente'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Modal de creación de cliente */}
        <Dialog open={createClientModalOpen} onOpenChange={setCreateClientModalOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Crear nuevo cliente</DialogTitle>
              <DialogDescription>
                Agrega un nuevo cliente al sistema y asígnalo a esta marca.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Nombre *</label>
                <Input
                  placeholder="Nombre del cliente"
                  value={newClientName}
                  onChange={(e) => setNewClientName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !creatingClient) {
                      handleCreateClient();
                    }
                  }}
                  disabled={creatingClient}
                  autoFocus
                  data-testid="new-client-nombre"
                />
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setCreateClientModalOpen(false);
                  setNewClientName('');
                }}
                disabled={creatingClient}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={handleCreateClient}
                disabled={creatingClient || !newClientName.trim()}
                className="bg-gray-900 hover:bg-gray-800"
                data-testid="create-client-submit"
              >
                {creatingClient ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creando...
                  </>
                ) : (
                  'Crear y asignar'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};
